import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { api } from "../../config/api";
import { Modal, Switch, Spin, Button } from "antd";
import { MailOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_BASE_URL } from "../../config/api";

const EmailSettings = ({ isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);

  // Fetch current email settings
  const { data: emailSettings, isLoading: fetchingSettings } = useQuery(
    ["emailSettings"],
    async () => {
      const response = await api.get(`${API_BASE_URL}/email-settings`);
      return response.data;
    },
    {
      enabled: isOpen, // Only fetch when modal is open
      refetchOnWindowFocus: false,
    }
  );

  // Update email settings mutation
  const updateEmailSettingsMutation = useMutation(
    async (enabled) => {
      const response = await api.post(`${API_BASE_URL}/email-settings`, { enabled });
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success("Email settings updated successfully!");
        queryClient.invalidateQueries(["emailSettings"]);
      },
      onError: (error) => {
        console.error("Error updating email settings:", error);
        toast.error(
          error?.response?.data?.message || "Failed to update email settings"
        );
      },
    }
  );

  const handleToggleEmail = (checked) => {
    updateEmailSettingsMutation.mutate(checked);
  };

  const emailEnabled = emailSettings?.enabled || false;

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <MailOutlined className="text-blue-600" />
          <span>Email Settings</span>
        </div>
      }
      open={isOpen}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
      width={500}
    >
      {fetchingSettings || updateEmailSettingsMutation.isLoading ? (
        <div className="flex justify-center items-center h-40">
          <Spin size="large" />
        </div>
      ) : (
        <div className="p-5 border rounded-lg shadow-sm">
          <div className="flex justify-between items-center mb-6">
            <div className="pr-4">
              <h3 className="text-lg font-medium">Email Service</h3>
              <p className="text-gray-500 whitespace-normal">
                Enable or disable email notifications from One Union Solutions Dashboard
              </p>
            </div>
            <Switch
              checked={emailEnabled}
              onChange={handleToggleEmail}
              className={`${emailEnabled ? "bg-blue-600" : ""} border-gray-300`}
              style={{ backgroundColor: emailEnabled ? undefined : "#e2e8f0" }}
            />
          </div>

          <div className="bg-gray-50 p-4 rounded-md">
            <h4 className="font-medium mb-2">Important Note:</h4>
            <p className="text-sm text-gray-600">
              When email service is disabled, all automated emails including welcome
              emails, notifications, and system alerts will not be sent to users.
              This affects all email functionality across the platform.
            </p>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default EmailSettings;